import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import type { Appointment, Calendar as CalendarT, Category, Service } from "@/api/types";
import { useStore } from "@/store/StoreContext";
import { Badge, Button, Input, tx } from "@/components/ui";
import { DateInput } from "@/components/DateInput";
import { AppointmentDrawer, type DrawerState } from "@/components/AppointmentDrawer";
import { FilterBar, type Filter } from "@/components/FilterBar";
import { WaitlistPanel } from "@/components/WaitlistPanel";
import { BlockDialog } from "@/components/BlockDialog";
import { addDays, todayIso } from "@/lib/dates";

const DENSITY = { compact: 1.0, normal: 1.6, large: 2.6 } as const;
type Density = keyof typeof DENSITY;

function minutesBetween(a: string | Date, b: string | Date) { return (new Date(b).getTime() - new Date(a).getTime()) / 60000; }
export function fmtTime(iso: string | Date, tz: string) { return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz }).format(typeof iso === "string" ? new Date(iso) : iso); }
/** Accent-insensitive, case-insensitive text key (Greek tonos/dialytika stripped, final sigma folded). */
export function canon(s: string) { return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/ς/g, "σ").trim(); }
const digits = (s: string) => s.replace(/\D/g, "");

export function CalendarPage() {
  const { store } = useStore();
  const { t, i18n } = useTranslation();
  const [sp, setSp] = useSearchParams();
  const [date, setDate] = useState(sp.get("date") ?? todayIso());
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [hover, setHover] = useState<{ staffId: string; mins: number } | null>(null);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [showWl, setShowWl] = useState(false);
  const [search, setSearch] = useState("");
  const [block, setBlock] = useState<{ staffId: string | null; time: string } | null>(null);
  const qc = useQueryClient();
  const delBlock = useMutation({ mutationFn: (id: string) => api(`/app/blocks/${id}`, { method: "DELETE" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["calendar"] }); qc.invalidateQueries({ queryKey: ["availability"] }); } });
  const [density, setDensity] = useState<Density>(() => { try { const d = localStorage.getItem("cal.density"); return d && d in DENSITY ? (d as Density) : "normal"; } catch { return "normal"; } });
  useEffect(() => { try { localStorage.setItem("cal.density", density); } catch { /* private mode */ } }, [density]);
  const px = DENSITY[density];
  const scroller = useRef<HTMLDivElement>(null);
  const filterQs = filters.length ? `&filters=${encodeURIComponent(JSON.stringify(filters))}` : "";
  const cal = useQuery({ queryKey: ["calendar", store?.id, date, filterQs], queryFn: () => api<CalendarT>(`/app/calendar?store_id=${store!.id}&date=${date}${filterQs}`), enabled: !!store });
  const services = useQuery({ queryKey: ["services", store?.id], queryFn: () => api<Service[]>(`/app/services?store_id=${store!.id}`), enabled: !!store });
  const serviceById = useMemo(() => new Map((services.data ?? []).map((s) => [s.id, s])), [services.data]);
  const categories = useQuery({ queryKey: ["categories", store?.id], queryFn: () => api<Category[]>(`/app/service-categories?store_id=${store!.id}`), enabled: !!store });
  const catById = useMemo(() => new Map((categories.data ?? []).map((c) => [c.id, c])), [categories.data]);
  const colorOf = (svc?: Service) => svc?.color ?? (svc?.category_id ? catById.get(svc.category_id)?.color : undefined);

  const data = cal.data;
  useEffect(() => {
    const id = sp.get("appt");
    if (!id || !data) return;
    const a = data.appointments.find((x) => x.id === id);
    if (a) { setDrawer({ mode: "edit", appointment: a }); setSp({}, { replace: true }); }
  }, [data, sp, setSp]);
  const vf = data ? new Date(data.view_from) : null;
  const vt = data ? new Date(data.view_to) : null;
  const open = data?.open ? new Date(data.open) : null;
  const close = data?.close ? new Date(data.close) : null;
  const totalMin = vf && vt ? minutesBetween(vf, vt) : 0;
  const hours: Date[] = [];
  if (vf && vt) for (let d = new Date(vf); d < vt; d = new Date(d.getTime() + 3600000)) hours.push(d);
  const y = (at: string | Date) => (vf ? minutesBetween(vf, at) * px : 0);

  // search: name, phone, service — non-matching appointments are dimmed, not removed
  const q = canon(search);
  const qDigits = digits(search);
  const matches = (a: Appointment) => {
    if (!q) return true;
    const name = canon(`${a.customer.first_name} ${a.customer.last_name}`);
    const svc = serviceById.get(a.service_id);
    const svcName = svc ? canon(`${svc.name.el ?? ""} ${svc.name.en ?? ""}`) : "";
    return name.includes(q) || svcName.includes(q) || (qDigits.length >= 3 && digits(a.customer.phone ?? "").includes(qDigits));
  };
  const matchCount = data ? data.appointments.filter(matches).length : 0;

  // keep the working part of the day in view: now (today) or the first shift
  useEffect(() => {
    if (!data || !vf || !scroller.current) return;
    const isToday = date === todayIso();
    const firstShift = data.columns.map((c) => c.shift_start).filter(Boolean).sort()[0];
    const target = isToday ? new Date(Date.now() - 30 * 60000) : firstShift ? new Date(firstShift) : open;
    if (!target) return;
    scroller.current.scrollTop = Math.max(0, minutesBetween(vf, target) * px - 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.date, data?.store_id, density]);

  const shift = (days: number) => setDate(addDays(date, days));
  if (!store) return <p className="text-gray-500">{t("app.loading")}</p>;

  const snap = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return Math.max(0, Math.floor((e.clientY - rect.top) / px / data!.grid_step) * data!.grid_step);
  };
  const onCellClick = (staffId: string, e: React.MouseEvent<HTMLDivElement>) => {
    if (!vf) return;
    const start = new Date(vf.getTime() + snap(e) * 60000);
    setDrawer({ mode: "create", staffId, date, time: fmtTime(start, data!.tz) });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-semibold mr-2">{t("nav.calendar")}</h1>
        <Button variant="ghost" onClick={() => shift(-1)}>‹</Button>
        <Button variant="ghost" onClick={() => setDate(todayIso())}>{t("calendar.today")}</Button>
        <Button variant="ghost" onClick={() => shift(1)}>›</Button>
        <DateInput value={date} onChange={(e) => setDate(e.target.value)} className="!w-auto" />
        <span className="text-sm text-gray-500">{data ? (q ? t("calendar.matches", { n: matchCount, total: data.appointments.length }) : `${data.appointments.length} ${t("calendar.appointments")}`) : ""}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="hidden md:flex gap-1 text-xs text-gray-500">{data?.resources.map((r) => <Badge key={r.id}>{tx(r.name, i18n.language)}: {r.units}</Badge>)}</div>
          <div className="flex rounded-lg border overflow-hidden text-xs">
            {(Object.keys(DENSITY) as Density[]).map((d) => <button key={d} type="button" className={`px-2 py-1 ${density === d ? "bg-brand text-white" : "bg-white hover:bg-gray-50"}`} onClick={() => setDensity(d)}>{t(`calendar.density.${d}`)}</button>)}
          </div>
          <Button variant="ghost" onClick={() => setBlock({ staffId: null, time: "" })}>{t("blocks.title")}</Button>
          <Button variant="ghost" onClick={() => setShowWl(!showWl)}>{t("wlp.title")}</Button>
          <Button onClick={() => setDrawer({ mode: "create", staffId: null, date, time: "" })}>{t("calendar.new")}</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder={t("calendar.search")} value={search} onChange={(e) => setSearch(e.target.value)} className="!w-72" />
        {search && <button type="button" className="text-xs underline" onClick={() => setSearch("")}>{t("views.clear")}</button>}
        <span className="text-xs text-gray-500">{t("calendar.hoverHint")}</span>
      </div>
      {store && <FilterBar brandId={store.brand_id} storeId={store.id} entity="calendar" filters={filters} onChange={setFilters} />}

      {data && !open && <p className="rounded-lg bg-amber-50 p-3 text-amber-800">{t("calendar.closed")}{data.closure_reason ? ` — ${data.closure_reason}` : ""}</p>}
      {data && open && data.closure_reason && <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">{t("calendar.specialHours")} — {data.closure_reason}</p>}
      <div className="flex flex-col lg:flex-row gap-4 items-start">
      {data && vf && vt && (
        <div ref={scroller} className="flex-1 min-w-0 w-full overflow-auto rounded-xl border bg-white" style={{ maxHeight: "calc(100vh - 230px)" }}>
          <div className="grid" style={{ gridTemplateColumns: `56px repeat(${data.columns.length}, minmax(160px, 1fr))` }}>
            <div className="sticky top-0 z-20 bg-white border-b" />
            {data.columns.map((c) => (
              <div key={c.staff_id} className="sticky top-0 z-20 border-b border-l bg-white p-2 text-center">
                <div className="font-medium" style={{ color: c.color }}>{c.display_name}</div>
                <div className="text-xs text-gray-500">{c.shift_start ? `${fmtTime(c.shift_start, data.tz)}–${fmtTime(c.shift_end!, data.tz)}` : t("calendar.off")}</div>
              </div>
            ))}
            <div className="sticky left-0 z-10 relative bg-white" style={{ height: totalMin * px }}>
              {hours.map((h) => (
                <div key={h.toISOString()} className="absolute right-1 text-xs text-gray-400" style={{ top: y(h) - 6 }}>{fmtTime(h, data.tz)}</div>
              ))}
            </div>
            {data.columns.map((c) => {
              const hv = hover && hover.staffId === c.staff_id ? hover.mins : null;
              return (
                <div key={c.staff_id} className="relative border-l cal-off cursor-pointer" style={{ height: totalMin * px }}
                  onClick={(e) => onCellClick(c.staff_id, e)}
                  onMouseMove={(e) => setHover({ staffId: c.staff_id, mins: snap(e) })}
                  onMouseLeave={() => setHover(null)}>
                  {open && close && <div className="absolute inset-x-0 bg-gray-100" style={{ top: y(open), height: minutesBetween(open, close) * px }} />}
                  {c.shift_start && <div className="absolute inset-x-0 bg-white" style={{ top: y(c.shift_start), height: minutesBetween(c.shift_start, c.shift_end!) * px }} />}
                  {hours.map((h) => (
                    <div key={h.toISOString()} className="absolute inset-x-0 border-t border-gray-200/80" style={{ top: y(h) }} />
                  ))}
                  {(data.blocks ?? []).filter((b) => !b.staff_id || b.staff_id === c.staff_id).map((b) => (
                    <div key={b.id} className="cal-block group absolute inset-x-0 z-[5] cursor-default overflow-hidden border-y border-gray-300" style={{ top: y(b.starts_at), height: minutesBetween(b.starts_at, b.ends_at) * px }} title={b.reason ?? t("blocks.blocked")} onClick={(e) => e.stopPropagation()} onMouseMove={(e) => e.stopPropagation()}>
                      <span className="absolute left-1 top-0.5 max-w-[80%] truncate text-[10px] text-gray-600">{b.reason ?? t("blocks.blocked")}{!b.staff_id ? ` · ${t("blocks.all")}` : ""}</span>
                      <button type="button" className="absolute right-1 top-0.5 rounded bg-white/90 px-1.5 text-[10px] font-medium text-gray-700 shadow hover:text-red-600" title={t("blocks.delete")} onClick={() => delBlock.mutate(b.id)}>✕ {t("blocks.unblock")}</button>
                    </div>
                  ))}
                  {hv !== null && (
                    <div className="pointer-events-none absolute inset-x-0 border-t-2 border-brand/70 bg-brand/10" style={{ top: hv * px, height: data.grid_step * px }}>
                      <span className="absolute -top-2.5 left-1 rounded bg-brand px-1.5 text-[11px] font-medium text-white">{fmtTime(new Date(vf.getTime() + hv * 60000), data.tz)}</span>
                    </div>
                  )}
                  {data.appointments.filter((a) => a.staff_id === c.staff_id).map((a) => (
                    <AppointmentBlock key={a.id} a={a} top={y(a.starts_at)} px={px} tz={data.tz} service={serviceById.get(a.service_id)} color={colorOf(serviceById.get(a.service_id))} lang={i18n.language} dim={!matches(a)}
                      onClick={(e) => { e.stopPropagation(); setDrawer({ mode: "edit", appointment: a }); }} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {showWl && <WaitlistPanel brandId={store.brand_id} storeId={store.id} date={date} tz={store.tz} services={services.data ?? []} onBook={(e, c) => setDrawer({ mode: "create", staffId: e.staff_id, date, time: "", customer: c, serviceId: e.service_id })} />}
      </div>
      <AppointmentDrawer state={drawer} onClose={() => setDrawer(null)} store={store} gridStep={data?.grid_step ?? 15} services={services.data ?? []} columns={data?.columns ?? []} />
      {block && <BlockDialog storeId={store.id} date={date} columns={data?.columns ?? []} staffId={block.staffId} time={block.time} onClose={() => setBlock(null)} />}
    </div>
  );
}

function AppointmentBlock({ a, top, px, tz, service, color, lang, dim, onClick }: { a: Appointment; top: number; px: number; tz: string; service?: Service; color?: string; lang: string; dim: boolean; onClick: (e: React.MouseEvent) => void }) {
  const height = Math.max(minutesBetween(a.starts_at, a.ends_at) * px, 18);
  const plain = a.status === "scheduled" && !a.conflict;
  const tone = a.status === "checked_in" ? "border-green-500 bg-green-50" : a.status === "completed" ? "border-gray-400 bg-gray-100" : a.status === "no_show" ? "border-red-400 bg-red-50" : a.conflict ? "border-amber-500 bg-amber-50" : color ? "" : "border-blue-500 bg-blue-50";
  const paint = plain && color ? { borderLeftColor: color, background: `${color}22` } : {};
  const compact = height < 30;
  return (
    <div className={`absolute inset-x-1 rounded-md border-l-4 p-1 text-xs shadow-sm overflow-hidden cursor-pointer transition-opacity ${tone} ${dim ? "opacity-25" : ""}`} style={{ top, height, ...paint }} onClick={onClick} title={`${a.customer.first_name} ${a.customer.last_name}${service ? ` · ${tx(service.name, lang)}` : ""}`}>
      <div className="font-medium truncate leading-tight">{fmtTime(a.starts_at, tz)}–{fmtTime(a.ends_at, tz)} {a.customer.vip ? "★ " : ""}{a.customer.first_name} {a.customer.last_name}{compact && service ? ` · ${tx(service.name, lang)}` : ""}</div>
      {!compact && <div className="truncate text-gray-600">{service ? tx(service.name, lang) : ""}{a.group_id ? " · duo" : ""}{a.conflict ? " · ⚠" : ""}</div>}
    </div>
  );
}
